import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Button, Typography, Box, Paper, Chip, CircularProgress, Grid, Dialog, DialogTitle, DialogContent,
  TextField, FormControl, Select, MenuItem, RadioGroup, FormControlLabel, Radio, IconButton
} from "@mui/material";
import { COLORS, TYPOGRAPHY, FONT_FAMILY } from "../../constants/color";
import { Icon } from "../../icons";
import axios from "axios";
import { url } from "../../constants/url";
import { useAuth } from "../../context/authContext";
import AppToast from "../../components/AppToast";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";

const EditFIRRequest = () => {
  const { token, hasRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const request = location.state?.request;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(true); // Always editing on this page
  const [toastConfig, setToastConfig] = useState({ open: false, type: "success", title: "", message: "" });
  const [pdfPreviewModal, setPdfPreviewModal] = useState({ open: false, url: "", title: "" });
  
  const [editFormData, setEditFormData] = useState({});

  const handlePreviewPdf = (fileUrl, title) => {
    setPdfPreviewModal({ open: true, url: fileUrl, title });
  };

  const parseBooleanString = (val, defaultVal = "true") => {
    if (val === true || val === "true" || val === "Yes" || val === "yes") return "true";
    if (val === false || val === "false" || val === "No" || val === "no") return "false";
    return defaultVal;
  };

  useEffect(() => {
    if (!request) {
      navigate(-1);
      return;
    }
    const firData = request.firDetails || request;
    setEditFormData({
      propertyType: firData.propertyType || request.propertyType || "",
      propertyName: firData.propertyName || request.projectName || "",
      buildingName: firData.buildingName || "",
      wing: firData.wing || "",
      floor: firData.floor || "",
      unitNumber: firData.unitNumber || "",
      fullAddress: firData.fullAddress || request.address || "",
      city: firData.city || "Pune",
      state: firData.state || "",
      pincode: firData.pincode || "",
      fullName: firData.fullName || request.clientName || "",
      mobileNumber: firData.mobileNumber || request.contactNumber || "",
      emailAddress: firData.emailAddress || "",
      altContactNumber: firData.altContactNumber || "",
      inspectionDateFrom: firData.inspectionDateFrom ? new Date(firData.inspectionDateFrom).toISOString().split('T')[0] : "",
      inspectionDateTo: firData.inspectionDateTo ? new Date(firData.inspectionDateTo).toISOString().split('T')[0] : "",
      preferredTimeSlot: firData.preferredTimeSlot || "",
      propertyStatus: firData.propertyStatus || "",
      purposeOfInspection: firData.purposeOfInspection || "",
      builderName: firData.builderName || "",
      projectName: firData.projectName || "",
      towerPhase: firData.towerPhase || "",
      reraNumber: firData.reraNumber || "",
      contactPersonName: firData.contactPersonName || "",
      contactPersonMobile: firData.contactPersonMobile || "",
      accessAvailable: parseBooleanString(firData.accessAvailable ?? firData.siteAccessAvailable ?? request.accessAvailable, "true"),
      securityGatePass: parseBooleanString(firData.securityGatePass ?? firData.securityGatePassRequired ?? request.securityGatePass, "true"),
      accessInstructions: firData.accessInstructions || "",
      landmark: firData.landmark || "",
      specialInstructions: firData.specialInstructions || "",
      areasOfConcern: firData.areasOfConcern || "",
      documents: firData.documents || [],
      firStatus: firData.firStatus || request.firStatus || "Pending",
    });
  }, [request, navigate]);

  const [filesToUpload, setFilesToUpload] = useState({});

  if (!request) return null;

  const showToast = (type, title, message) => setToastConfig({ open: true, type, title, message });


  const handleFieldChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (field, e) => {
    if (e.target.files && e.target.files[0]) {
      setFilesToUpload((prev) => ({ ...prev, [field]: e.target.files[0] }));
    }
  };

  const handleSave = async () => {
    const firId = (request.firDetails?._id) || request._id;
    try {
      setSaving(true);
      
      const formData = new FormData();
      Object.keys(editFormData).forEach(key => {
        if (key === 'documents') return;
        formData.append(key, editFormData[key]);
      });

      Object.keys(filesToUpload).forEach(key => {
        formData.append(key, filesToUpload[key]);
      });

      const res = await axios.put(`${url}/api/fir/requests/update/${firId}`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data" 
        }
      });

      if (res.data.success) {
        showToast("success", "Success", "FIR Request updated successfully.");
        setTimeout(() => navigate(-1), 1500);
      }
    } catch (error) {
      showToast("error", "Error", error.response?.data?.message || "Failed to update FIR request.");
    } finally {
      setSaving(false);
    }
  };

  const labelStyle = {
    fontFamily: FONT_FAMILY,
    ...TYPOGRAPHY.H4,
    color: COLORS.PRIMARY,
    mb: 1,
    display: "block"
  };

  const inputStyle = {
    width: "100%",
    maxWidth: "321px",
    "&.MuiFormControl-root": { maxWidth: "321px !important" },
    "&.MuiTextField-root": { maxWidth: "321px !important" },
    "& .MuiOutlinedInput-root": {
      height: "50px",
      borderRadius: "7px",
      "& fieldset": { borderWidth: "1px", borderColor: COLORS.GREY_COLOR },
      "&:hover fieldset": { borderColor: COLORS.SECONDARY },
      "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
      "& input": { color: COLORS.PRIMARY, fontFamily: FONT_FAMILY }
    },
    "& .MuiSelect-select": {
      display: "flex", alignItems: "center", padding: "0 14px", height: "50px", boxSizing: "border-box", color: COLORS.PRIMARY, fontFamily: FONT_FAMILY
    }
  };

  const autoGrowStyle = {
    ...inputStyle,
    "& .MuiOutlinedInput-root": {
      minHeight: "50px",
      height: "auto",
      borderRadius: "7px",
      padding: "13px 14px",
      "& fieldset": { borderWidth: "1px", borderColor: COLORS.GREY_COLOR },
      "&:hover fieldset": { borderColor: COLORS.SECONDARY },
      "&.Mui-focused fieldset": { borderColor: COLORS.SECONDARY },
      "& textarea": { color: COLORS.PRIMARY, fontFamily: FONT_FAMILY, padding: 0 }
    }
  };

  const rowContainerStyle = {
    display: "grid",
    gridTemplateColumns: {
      xs: "100%",
      sm: "repeat(2, 1fr)",
      md: "repeat(3, 1fr)"
    },
    gap: "24px",
    mb: "28px",
    width: "100%"
  };

  const columnContainerStyle = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    minWidth: 0
  };

  const sectionPaperStyle = {
    p: { xs: 2, md: 4 },
    mb: 4,
    borderRadius: "16px",
    boxShadow: "0 8px 32px rgba(0,0,0,0.04)",
    backgroundColor: "#fff",
    border: `1px solid ${COLORS.LIGHT_SECONDARY}40`
  };

  const sectionHeaderStyle = {
    fontFamily: FONT_FAMILY,
    ...TYPOGRAPHY.H4,
    color: COLORS.PRIMARY,
    fontWeight: 700,
    mb: 3,
  };

  const SectionTitle = ({ title }) => (
    <>
      <Typography sx={sectionHeaderStyle}>{title}</Typography>
      <Box sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}40`, mb: 3 }} />
    </>
  );

  const defaultTimeSlots = ["10:00 AM - 01:00 PM", "02:00 PM - 05:00 PM", "10:00 AM - 05:00 PM", "09:00 AM - 12:00 PM", "01:00 PM - 04:00 PM"];
  const timeSlotOptions = editFormData.preferredTimeSlot && !defaultTimeSlots.includes(editFormData.preferredTimeSlot)
    ? [editFormData.preferredTimeSlot, ...defaultTimeSlots]
    : defaultTimeSlots;

  const EditItem = ({ label, field, type = "text", options = null, multiline = false, rows = 2 }) => (
    <Box sx={columnContainerStyle}>
      <Typography sx={labelStyle}>{label}</Typography>
      {options ? (
        <FormControl fullWidth size="small" sx={inputStyle}>
          <Select value={editFormData[field] || ""} onChange={(e) => handleFieldChange(field, e.target.value)}>
            {options.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : type === "date" ? (
        <TextField
          fullWidth
          size="small"
          type="date"
          sx={inputStyle}
          value={editFormData[field] || ""}
          onChange={(e) => handleFieldChange(field, e.target.value)}
          onClick={(e) => e.target.showPicker && e.target.showPicker()}
        />
      ) : (
        <TextField
          fullWidth
          size="small"
          multiline={multiline}
          minRows={multiline ? 1 : undefined}
          maxRows={multiline ? 5 : undefined}
          sx={multiline ? autoGrowStyle : inputStyle}
          value={editFormData[field] || ""}
          onChange={(e) => handleFieldChange(field, e.target.value)}
        />
      )}
    </Box>
  );

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, width: '100%', boxSizing: 'border-box' }}>
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
        <IconButton onClick={() => navigate(-1)} sx={{ backgroundColor: 'white', boxShadow: '0 2px 8px rgba(0,0,0,0.05)' }}>
          <ArrowBackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY, m: 0 }}>
            Edit FIR Request Details
          </Typography>
          <Typography sx={{ color: COLORS.MUTED_TEXT, mt: 0.5 }}>
            Update the property and client details for this request.
          </Typography>
        </Box>
        <Chip label={editFormData.firStatus} sx={{ backgroundColor: COLORS.PRIMARY, color: 'white', fontWeight: 600 }} />
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        
        {/* 1. Property Details */}
        <Paper sx={sectionPaperStyle} elevation={0}>
          <SectionTitle title="1. Property Details" />
          <Box sx={rowContainerStyle}>
            <EditItem label="Property Type" field="propertyType" />
            <EditItem label="Property Name" field="propertyName" />
            <EditItem label="Building / Society Name" field="buildingName" />
            <EditItem label="Wing / Tower" field="wing" />
            <EditItem label="Floor" field="floor" />
            <EditItem label="Unit / Flat No" field="unitNumber" />
            <Box sx={{ gridColumn: { xs: '1 / -1', md: 'span 3' } }}>
              <EditItem label="Full Address" field="fullAddress" multiline rows={2} />
            </Box>
            <EditItem label="City" field="city" />
            <EditItem label="State" field="state" />
            <EditItem label="PIN Code" field="pincode" />
          </Box>
        </Paper>

        {/* 2. Client Details */}
        <Paper sx={sectionPaperStyle} elevation={0}>
          <SectionTitle title="2. Client Details" />
          <Box sx={rowContainerStyle}>
            <EditItem label="Full Name" field="fullName" />
            <EditItem label="Mobile Number" field="mobileNumber" />
            <EditItem label="Email Address" field="emailAddress" />
            <EditItem label="Alternate Contact" field="altContactNumber" />
          </Box>
        </Paper>

        {/* 3. Inspection Details */}
        <Paper sx={sectionPaperStyle} elevation={0}>
          <SectionTitle title="3. Inspection Details" />
          <Box sx={rowContainerStyle}>
            <EditItem label="Preferred Date From" field="inspectionDateFrom" type="date" />
            <EditItem label="Preferred Date To" field="inspectionDateTo" type="date" />
            <EditItem label="Preferred Time Slot" field="preferredTimeSlot" options={timeSlotOptions} />
            <EditItem label="Property Status" field="propertyStatus" options={["Under Construction", "Ready Possession", "Renovation", "Occupied"]} />
            <EditItem label="Purpose of Inspection" field="purposeOfInspection" options={["Before Possession", "During Construction", "Quality Check", "Snag Inspection", "Other"]} />
          </Box>
        </Paper>

        {/* 4. Builder / Project Info */}
        <Paper sx={sectionPaperStyle} elevation={0}>
          <SectionTitle title="4. Builder / Project Information" />
          <Box sx={rowContainerStyle}>
            <EditItem label="Builder Name" field="builderName" />
            <EditItem label="Project Name" field="projectName" />
            <EditItem label="Tower / Phase" field="towerPhase" />
            <EditItem label="RERA Number" field="reraNumber" />
          </Box>
        </Paper>

        {/* 5. Site Contact Person */}
        <Paper sx={sectionPaperStyle} elevation={0}>
          <SectionTitle title="5. Site Contact Person" />
          <Box sx={rowContainerStyle}>
            <EditItem label="Contact Person Name" field="contactPersonName" />
            <EditItem label="Mobile Number" field="contactPersonMobile" />
          </Box>
        </Paper>

        {/* 6. Property Access */}
        <Paper sx={sectionPaperStyle} elevation={0}>
          <SectionTitle title="6. Property Access" />
          <Box sx={rowContainerStyle}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ color: COLORS.PRIMARY, fontSize: "12px", fontWeight: 600 }}>Site Access Available</Typography>
              <RadioGroup row value={editFormData.accessAvailable ?? ""} onChange={(e) => handleFieldChange("accessAvailable", e.target.value)}>
                <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <Typography sx={{ color: COLORS.PRIMARY, fontSize: "12px", fontWeight: 600 }}>Security Gate Pass Required</Typography>
              <RadioGroup row value={editFormData.securityGatePass ?? ""} onChange={(e) => handleFieldChange("securityGatePass", e.target.value)}>
                <FormControlLabel value="true" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="false" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </Box>
            <EditItem label="Access Instructions" field="accessInstructions" />
            <EditItem label="Landmark" field="landmark" />
          </Box>
        </Paper>

        {/* 7. Additional Info */}
        <Paper sx={sectionPaperStyle} elevation={0}>
          <SectionTitle title="7. Additional Information" />
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: '24px' }}>
            <EditItem label="Special Instructions" field="specialInstructions" />
            <EditItem label="Areas of Concern" field="areasOfConcern" />
          </Box>
        </Paper>

        {/* 8. Uploaded Documents & PDF Preview */}
        <Paper sx={sectionPaperStyle} elevation={0}>
          <SectionTitle title="8. Uploaded Documents & PDF Preview" />
          {editFormData.documents && editFormData.documents.length > 0 ? (
            <Grid container spacing={2}>
              {editFormData.documents.map((doc, idx) => {
                const docUrl = doc.url?.startsWith("http") ? doc.url : `${url}${doc.url}`;
                return (
                  <Grid item xs={12} sm={6} md={3} key={idx}>
                    <Paper
                      elevation={0}
                      sx={{
                        p: 2,
                        borderRadius: "10px",
                        border: `1px solid ${COLORS.LIGHT_SECONDARY}40`,
                        backgroundColor: "#F9F9FB",
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <PictureAsPdfIcon sx={{ color: "error.main", fontSize: 28 }} />
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography
                            sx={{
                              fontSize: "13px",
                              fontWeight: 700,
                              color: COLORS.PRIMARY,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {doc.documentType || `Document ${idx + 1}`}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: COLORS.MUTED_TEXT,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {doc.name || "Uploaded PDF"}
                          </Typography>
                        </Box>
                      </Box>
                      <Box display="flex" gap={1}>
                        <Button
                          fullWidth
                          size="small"
                          variant="contained"
                          startIcon={<VisibilityIcon fontSize="small" />}
                          onClick={() => handlePreviewPdf(docUrl, doc.documentType || doc.name)}
                          sx={{
                            backgroundColor: COLORS.PRIMARY,
                            color: "#fff",
                            fontSize: "11px",
                            fontWeight: 700,
                            textTransform: "none",
                            borderRadius: "6px",
                            "&:hover": { backgroundColor: COLORS.SECONDARY },
                          }}
                        >
                          Preview
                        </Button>
                        <IconButton
                          size="small"
                          component="a"
                          href={docUrl}
                          target="_blank"
                          download
                          sx={{
                            border: `1px solid ${COLORS.LIGHT_SECONDARY}40`,
                            borderRadius: "6px",
                            color: COLORS.PRIMARY,
                          }}
                        >
                          <DownloadIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </Paper>
                  </Grid>
                );
              })}
            </Grid>
          ) : (
            <Typography sx={{ color: COLORS.MUTED_TEXT, fontSize: "14px", fontStyle: "italic", mb: 2 }}>
              No documents currently uploaded.
            </Typography>
          )}

          <Box mt={4}>
            <Typography sx={{ fontFamily: FONT_FAMILY, fontWeight: 600, color: COLORS.PRIMARY, mb: 2, fontSize: "15px" }}>
              Upload New Documents (Replaces existing if same type)
            </Typography>
            <Grid container spacing={3}>
              {[
                { field: "floorPlan", label: "Floor Plan" },
                { field: "agreementCopy", label: "Agreement Copy" },
                { field: "builderLayout", label: "Builder Layout" },
                { field: "previousReport", label: "Previous Inspection Report" }
              ].map((docType) => (
                <Grid item xs={12} sm={6} md={3} key={docType.field}>
                  <Box
                    sx={{
                      border: `1px dashed ${COLORS.GREY_COLOR}`,
                      borderRadius: "8px",
                      p: 2,
                      textAlign: "center",
                      backgroundColor: "#FAFAFA",
                      position: "relative",
                      transition: "0.2s",
                      "&:hover": { borderColor: COLORS.SECONDARY, backgroundColor: "#F0F4FF" }
                    }}
                  >
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: COLORS.TEXT, mb: 1 }}>
                      {docType.label}
                    </Typography>
                    
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleFileChange(docType.field, e)}
                      style={{
                        position: "absolute",
                        top: 0, left: 0, width: "100%", height: "100%",
                        opacity: 0, cursor: "pointer"
                      }}
                    />
                    
                    <Box display="flex" flexDirection="column" alignItems="center" gap={0.5}>
                      <Icon name="Upload" size={20} color={filesToUpload[docType.field] ? COLORS.SECONDARY : COLORS.MUTED_TEXT} />
                      <Typography sx={{ fontSize: "11px", color: filesToUpload[docType.field] ? COLORS.SECONDARY : COLORS.MUTED_TEXT, fontWeight: 500 }}>
                        {filesToUpload[docType.field] ? filesToUpload[docType.field].name : "Click to select file"}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>

        <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
          <Button onClick={() => navigate(-1)} variant="outlined" sx={{ borderColor: COLORS.GREY_COLOR, color: COLORS.TEXT, px: 4 }}>
            Cancel
          </Button>
          <Button onClick={handleSave} variant="contained" disabled={saving} sx={{ backgroundColor: COLORS.PRIMARY, color: "white", px: 4 }}>
            {saving ? <CircularProgress size={24} color="inherit" /> : "Save Changes"}
          </Button>
        </Box>
      </Box>

      {/* PDF Preview Modal */}
      <Dialog
        open={pdfPreviewModal.open}
        onClose={() => setPdfPreviewModal({ open: false, url: "", title: "" })}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { borderRadius: "12px", height: "80vh" },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontFamily: FONT_FAMILY,
            color: COLORS.PRIMARY,
            fontWeight: 700,
            borderBottom: `1px solid ${COLORS.BORDER}`,
            py: 1.5,
          }}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <PictureAsPdfIcon sx={{ color: "error.main" }} />
            <Typography variant="h6" fontWeight={700}>
              {pdfPreviewModal.title || "PDF Document Preview"}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {pdfPreviewModal.url && (
              <Button
                size="small"
                variant="outlined"
                href={pdfPreviewModal.url}
                target="_blank"
                sx={{ textTransform: "none", fontSize: "12px" }}
              >
                Open in New Tab
              </Button>
            )}
            <IconButton
              onClick={() => setPdfPreviewModal({ open: false, url: "", title: "" })}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, height: "100%", backgroundColor: "#525659" }}>
          {pdfPreviewModal.url ? (
            <iframe
              src={pdfPreviewModal.url}
              title="PDF Preview"
              width="100%"
              height="100%"
              style={{ border: "none" }}
            />
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <Typography color="white">No document preview available.</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <AppToast open={toastConfig.open} onClose={() => setToastConfig(p => ({...p, open: false}))} type={toastConfig.type} title={toastConfig.title} message={toastConfig.message} />
    </Box>
  );
};

export default EditFIRRequest;
